// todo: Review this file, it has too many helper methods in it used out of order
/* eslint-disable @typescript-eslint/no-use-before-define */
import { UseFormTools } from "@wisr/common";
import {
  Button,
  ButtonRow,
  Field,
  Fieldset,
  StatusCallout,
} from "@wisr/react-ui";
import React, { useState } from "react";

import { RemoveButton } from "../../shared/remove-button/remove-button.component";
import { Application, Frequency, Status } from "../application";
import { applicationFormDefaults } from "../application-form.constants";
import formCardStyle from "./form-card.scss";

export const defaultIncomeSource = {
  label: "Main Job - Total Salary and Wages",
  value: "Salary & Wages",
};
const incomeSourceOptions = [
  defaultIncomeSource,
  "Rental Income",
  "Self employed Business Income",
  "Government Benefits",
  "Investment Income - Interest",
  "Investment Income - Dividends",
  "Other Job",
];

// Currently we only support otherIncome_<1-3>
type OtherIncomeId = 1 | 2 | 3;

type Income = {
  amount: string;
  frequency: string;
  source: string;
};
const otherIncomeIds: OtherIncomeId[] = [1, 2, 3];
// The last item in the array is assumed to be the maximum count
const [maxOtherIncomeCount] = otherIncomeIds.slice(-1);
const incomeFrequencyOptions = Object.values(Frequency).map((f) =>
  f.toString()
);

interface Props {
  fieldAdapter: UseFormTools<typeof applicationFormDefaults>["fieldAdapter"];
  formData: Application;
  setValue: UseFormTools<typeof applicationFormDefaults>["setValue"];
  callouts?: Status[] | null;
}
export const ApplicationIncomeComponent: React.FunctionComponent<Props> = ({
  fieldAdapter,
  formData,
  setValue,
  callouts,
}) => {
  const { otherIncomeSource_1, otherIncomeSource_2, otherIncomeSource_3 } =
    formData;
  const [otherIncomeCount, setOtherIncomeCount] = useState(() => {
    switch (true) {
      case !!otherIncomeSource_3:
        return 3;
      case !!otherIncomeSource_2:
        return 2;
      case !!otherIncomeSource_1:
        return 1;
      default:
        return 0;
    }
  });

  function addOtherIncome() {
    setOtherIncomeCount(otherIncomeCount + 1);
  }

  function removeOtherIncome(remove: OtherIncomeId) {
    return () => {
      const incomes = removeIncome(getIncomeFields(formData), remove - 1);
      incomes.forEach((income, i) => {
        const { amountKey, frequencyKey, sourceKey } = getFieldKeys(
          (i + 1) as OtherIncomeId
        );
        setValue(amountKey, income.amount);
        setValue(frequencyKey, income.frequency);
        setValue(sourceKey, income.source);
      });

      for (let i = incomes.length + 1; i <= 3; i++) {
        const { amountKey, frequencyKey, sourceKey } = getFieldKeys(
          i as OtherIncomeId
        );
        setValue(amountKey, "");
        setValue(frequencyKey, "");
        setTimeout(() => setValue(sourceKey, ""));
      }

      setOtherIncomeCount(incomes.length);
    };
  }

  return (
    <>
      <Fieldset heading="What you earn">
        <>
          {callouts &&
            callouts.map((callout, i) => (
              <StatusCallout
                key={`${i}-callout`}
                type={callout.kind}
                message={callout.message}
              />
            ))}
        </>

        <FormCard heading="Primary income">
          <Field
            type="select"
            label="Income source"
            options={incomeSourceOptions}
            placeholder="Select..."
            {...fieldAdapter({
              name: "incomeSource",
              required: "Income source is required",
            })}
          ></Field>

          <Field
            type="currency"
            label="Income amount (before tax)"
            {...fieldAdapter({
              name: "incomeAmount",
              required: "Income amount is required",
              min: [0],
            })}
          ></Field>

          <Field
            type="select"
            label="Income frequency"
            options={incomeFrequencyOptions}
            disabled
            placeholder="Select..."
            {...fieldAdapter({
              name: "incomeFrequency",
              required: "Income frequency is required",
            })}
          ></Field>
        </FormCard>

        <>
          {otherIncomeIds.map((id) => {
            const { amountKey, frequencyKey, sourceKey } = getFieldKeys(id);

            return (
              <FormCard
                hide={id > otherIncomeCount}
                key={id}
                heading={`Other income source ${id}`}
                action={<RemoveButton onClick={removeOtherIncome(id)} />}
              >
                <Field
                  type="select"
                  label="Income source"
                  options={incomeSourceOptions}
                  placeholder="Select..."
                  {...fieldAdapter({
                    name: sourceKey,
                    required: "Income source is required",
                    condition: id <= otherIncomeCount,
                  })}
                ></Field>

                <Field
                  type="currency"
                  label="Income amount (before tax)"
                  {...fieldAdapter({
                    name: amountKey,
                    required: "Income amount is required",
                    min: [1],
                    condition: id <= otherIncomeCount,
                  })}
                ></Field>

                <Field
                  type="select"
                  label="Income frequency"
                  options={incomeFrequencyOptions}
                  placeholder="Select..."
                  {...fieldAdapter({
                    name: frequencyKey,
                    required: "Income frequency is required",
                    condition: id <= otherIncomeCount,
                  })}
                ></Field>
              </FormCard>
            );
          })}

          {otherIncomeCount < maxOtherIncomeCount && (
            <ButtonRow>
              <Button button="secondary">
                <button type="button" onClick={addOtherIncome}>
                  Add other income
                </button>
              </Button>
            </ButtonRow>
          )}
        </>

        <Fieldset
          heading="Your partner's income"
          hide={!hasPartner(formData.maritalStatus)}
        >
          <p>
            If you partner has an income, please tell us about it here. This
            helps us determine if this loan is affordable for you.
          </p>
          <Field
            label="Partner's total annual income (before tax)"
            type="currency"
            {...fieldAdapter({
              name: "partnerIncomeAmount",
              condition: hasPartner(formData.maritalStatus),
              min: [0],
            })}
          />
        </Fieldset>
      </Fieldset>
    </>
  );
};

export function hasPartner(maritalStatus: string) {
  return ["Married", "De Facto"].includes(maritalStatus);
}

function getFieldKeys(id: OtherIncomeId) {
  /**
   * @todo the version of TS we are using does not support literal types
   * If it did, we would be able to use `as const` and it would resolve the types correctly.
   * When we move to a newer version of TS we should make this change
   * */
  const amountKey = `otherIncomeAmount_${id}` as
    | "otherIncomeAmount_1"
    | "otherIncomeAmount_2"
    | "otherIncomeAmount_3";
  const frequencyKey = `otherIncomeFrequency_${id}` as
    | "otherIncomeFrequency_1"
    | "otherIncomeFrequency_2"
    | "otherIncomeFrequency_3";
  const sourceKey = `otherIncomeSource_${id}` as
    | "otherIncomeSource_1"
    | "otherIncomeSource_2"
    | "otherIncomeSource_3";

  return { amountKey, frequencyKey, sourceKey };
}

/**
 * @todo this is a temporary component until it becomes part of the design system propper
 * When this happens it should be renamed to match with the design system and then
 * unit tests will need to be written
 */
export const FormCard: React.FunctionComponent<{
  heading: string;
  action?: React.ReactNode;
  hide?: boolean;
}> =
  /* istanbul ignore next */
  ({ heading, action: remove, children, hide }) => (
    <section className={hide ? "card-hidden" : ""}>
      <style jsx>{formCardStyle}</style>
      <p>
        {heading}
        {remove}
      </p>
      <div>{children}</div>
    </section>
  );

const getIncomeFields = (formData: Application): Income[] => {
  const incomes: Income[] = [];
  for (let i = 1; i <= 3; i++) {
    const { amountKey, frequencyKey, sourceKey } = getFieldKeys(
      i as OtherIncomeId
    );
    incomes.push({
      amount: formData[amountKey],
      frequency: formData[frequencyKey],
      source: formData[sourceKey],
    });
  }
  return incomes.filter((income) => income.source !== "");
};

const removeIncome = (array: Array<Income>, index: number): Income[] => {
  return array.slice(0, index).concat(array.slice(index + 1));
};
